import { Address, AddressSuggestion } from '@ideal-postcodes/api-typings';
import { Button, IAutoCompleteOption, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { Uprn } from '../../../../shared/api-clients/_api';
import { AutoCompleteAddressInput } from '../../../../shared/components/autoCompleteAddressInput';
import { useRoutePaths } from '../../../../shared/context/paths';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { useBee } from '../../../../shared/hooks/useBee';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { patternValidator, requiredTextInputValidator } from '../../../../shared/hooks/Validators';
import {
  formatAddressFromFormValues,
  updateFormWithIdealPostcodesAddress,
  useAddressUdprnLookup,
} from '../../../../shared/services/addressLookupService';
import { IPropertyAddress } from '../../../../shared/types';
import { BackButton } from '../../components/buttons/back-button';
import { routePaths } from '../../routes';
import './address.scss';
import { Spinner } from '../../components/spinner/spinner';

interface IAddressForm extends IPropertyAddress {
  autoCompleteAddressValue: string;
}

export const validators: InputValidator<IAddressForm>[] = [
  {
    key: 'addressLine1',
    validators: [requiredTextInputValidator],
  },
  {
    key: 'city',
    validators: [requiredTextInputValidator],
  },
  {
    key: 'postcode',
    validators: [
      requiredTextInputValidator,
      patternValidator(/^[a-zA-Z]{1,2}\d[a-zA-Z\d]?\s*\d[a-zA-Z]{2}$/, 'Please enter a valid postcode'),
    ],
  },
];

export const AddressView: React.FunctionComponent = () => {
  const { beeInfo, updateBee, loading } = useBee();
  const { push } = useRoutePaths();
  const formState = React.useMemo(
    () => ({
      autoCompleteAddressValue: undefined,
      ...beeInfo?.bee?.physicalAddress,
    }),
    [beeInfo?.bee?.physicalAddress]
  );
  const { DataForm, bind, dataBinder, notifyChange } = useForm(formState);
  const { formIsValid, validationErrors } = useValidatedForm<IAddressForm>(validators, dataBinder);

  const addressUdprnLookup = useAddressUdprnLookup();

  const updateAddressFromAutoComplete = async (selectedOption: IAutoCompleteOption) => {
    const selectedSuggestedAddress: AddressSuggestion = selectedOption?.data;
    const address: Address = await addressUdprnLookup(selectedSuggestedAddress?.udprn);
    updateFormWithIdealPostcodesAddress<IPropertyAddress>(dataBinder, address);
    notifyChange();
  };

  const addressFormValid = formIsValid || validationErrors.length === 0;

  const displayedAddress = React.useMemo(() => {
    return formatAddressFromFormValues<IPropertyAddress>(dataBinder);
  }, [dataBinder.getKeyValue('addressLine1')]);

  const canContinue = addressFormValid && !loading && !!displayedAddress;
  const handleContinue = async () => {
    const physicalAddress = dataBinder.toJson() as IPropertyAddress;
    const uprn = physicalAddress?.uprn?.length === 0 ? undefined : (physicalAddress?.uprn as Uprn);
    await updateBee({ deviceId: beeInfo.bee.deviceId, physicalAddress, ...(uprn && { uprn }) }); // Only add uprn if it is not empty
    push(routePaths.plot.root);
  };

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Property <span className="color-primary">Address</span>
      </h1>
      <div>
        <AutoCompleteAddressInput
          data-qa="address-autocomplete"
          {...bind.autoCompleteSingleInput('autoCompleteAddressValue')}
          onChange={updateAddressFromAutoComplete}
        />

        <h2 data-qa="displayed-address">{displayedAddress}</h2>
      </div>
      <div className="row">
        <span className="spacer" />
        <Button
          data-qa="continue-button"
          className="continue-button btn-full btn-large"
          disabled={!canContinue}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>
    </DataForm>
  );
};
